import { Typography } from '@euler/app/components/typography';
import { LoginHeader } from '@euler/app/flows/auth/components/LoginHeader';
import { LoginScreenView } from '@euler/app/flows/auth/components/LoginScreenView';
import { useAuthTheme, useLogin } from '@euler/app/flows/auth/hooks';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { TVFocusable } from '@euler/components/tvos/TVFocusable';
import { config } from '@euler/config';
import { isHubBuild } from '@euler/functions/isHub';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useLayoutContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AuthProviderType } from '@euler/model/auth';
import { QrcodeAuthChallengeResponse } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, makeDebug, showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useTimer } from '@euler/utils/hooks/useTimer';
import * as Device from 'expo-device';
import { memo, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';

const debug = makeDebug('qrcode-auth-challenge', __DEV__);

type Status = 'scanned' | 'confirmed';

type Message =
  | {
      type: 'scanned';
    }
  | {
      type: 'confirmed';
      data: string;
    }
  | {
      type: 'canceled';
    };

export const QrcodeLoginScreen = wrapNavigatorScreen(() => {
  const { rpx } = useResponsiveContext();
  const { authService } = useServiceFactory();
  const appLoading = useAppLoading();
  const timer = useTimer();
  const theme = useAuthTheme();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState<QrcodeAuthChallengeResponse>();
  const clientId = challenge?.clientId;
  const socketRef = useRef<WebSocket>();
  const [status, setStatus] = useState<Status>();
  const { login } = useLogin();

  const beginChallenge = usePersistFn(() => {
    setChallenge(undefined);
    setLoading(true);
    const deviceName =
      Platform.OS === 'web'
        ? 'web'
        : Device.modelName ??
          Device.deviceName ??
          Device.manufacturer ??
          (isHubBuild() ? '知车大屏端' : '设备');
    authService
      .beginQrcodeAuthChallenge(deviceName, { margin: 2 })
      .then(resp => {
        setChallenge(resp);
      })
      .catch(err => {
        void showAlert((err as any).message);
      })
      .finally(() => {
        setLoading(false);
      });
  });

  useEffect(() => {
    beginChallenge();
  }, [beginChallenge]);

  const loginWithTrustedToken = usePersistFn(async (authToken: string) => {
    await appLoading.showWithAction(async () => {
      await login({
        type: AuthProviderType.TrustedAuthorization,
        authToken,
      });
    });
  });

  useEffect(() => {
    if (!clientId) return;

    const url = `${config.wsEndPoint}?clientid=${clientId}`;
    const socket = new WebSocket(url);
    socketRef.current = socket;
    socket.addEventListener('open', () => {
      debug('opened websocket connection to %s', url);
    });
    socket.addEventListener('message', e => {
      const msg = JSON.parse(e.data) as Message;
      if (msg.type === 'scanned') {
        timer.clear();
        setStatus('scanned');
      } else if (msg.type === 'confirmed') {
        timer.clear();
        setStatus('confirmed');
        delayed(() => {
          void loginWithTrustedToken(msg.data);
        }, 500);
      } else if (msg.type === 'canceled') {
        timer.clear();
        setStatus(undefined);
        beginChallenge();
      }
    });
    socket.addEventListener('error', e => {
      debug(
        'an error occurred while connecting to %s: ',
        url,
        (e as any).message,
      );
    });
    socket.addEventListener('close', () => {
      debug('websocket connection closed');
    });
    return () => {
      socket.close();
      socketRef.current = undefined;
    };
  }, [beginChallenge, clientId, loginWithTrustedToken, timer]);

  useEffect(() => {
    timer.clear();

    if (!challenge) return;

    const ttl = challenge.ttl;

    timer.schedule(
      () => {
        beginChallenge();
      },
      (ttl - 3) * 1000,
    );
  }, [challenge, beginChallenge, timer]);

  return (
    <LoginScreenView>
      <LayoutProviderView
        css={`
          padding: ${rpx(18)}px ${rpx(32)}px;
          align-self: stretch;
          flex: 1;
        `}
      >
        <LoginHeader
          title={'APP扫码登录'}
          subTitle={'打开知车APP“扫一扫”下面二维码快速授权登录'}
          qrcodeButton={false}
          subTitleStyle={{ textAlign: 'center' }}
        />
        <View
          css={`
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <QrcodeView
            loading={loading}
            challenge={challenge}
            css={`
              margin-top: ${rpx(16)}px;
              margin-bottom: ${rpx(16)}px;
            `}
          />
          {status ? (
            <HStack>
              <Icon
                name="checkmark-circle"
                color={StatusColors.Success}
                size={rpx(16)}
              />
              <Typography.Label
                family="regular"
                color={StatusColors.Success}
                size={rpx(14)}
                lineHeight={rpx(18)}
                css={`
                  margin-left: ${rpx(4)}px;
                `}
              >
                {status === 'scanned' ? '已扫码，等待确认授权登录' : '登录成功'}
              </Typography.Label>
            </HStack>
          ) : (
            <TVFocusable onPress={beginChallenge}>
              <Typography.Label
                light
                color={theme.linkTextColor}
                size={rpx(16)}
              >
                刷新二维码
              </Typography.Label>
            </TVFocusable>
          )}
        </View>
      </LayoutProviderView>
    </LoginScreenView>
  );
});

const QrcodeView = memo(
  ({
    challenge,
    loading,
    style,
  }: {
    loading: boolean;
    style?: StyleProp<ViewStyle>;
    challenge: QrcodeAuthChallengeResponse | undefined;
  }) => {
    const { height } = useWindowDimensions();
    const containerWidth = useLayoutContainerWidth();
    const width = Math.min(containerWidth * 0.65, height * 0.35);

    return (
      <View
        style={[
          {
            width,
            height: width,
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            borderRadius: 15,
            overflow: 'hidden',
          },
          style,
        ]}
      >
        {loading ? (
          <Center style={StyleSheet.absoluteFill}>
            <ActivityIndicator />
          </Center>
        ) : challenge ? (
          <Image
            source={{ uri: challenge.dataURI }}
            style={{ width, height: width }}
          />
        ) : null}
      </View>
    );
  },
);
